import { ApiResponse } from '@/types'

class ApiClient {
  private baseURL: string
  private defaultHeaders: Record<string, string>

  constructor() {
    this.baseURL = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321'
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
  }

  private getAuthHeaders(): Record<string, string> {
    // For admin endpoints, use access_token from localStorage
    const token = localStorage.getItem('access_token')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('content-type')
    const isJson = contentType?.includes('application/json')

    let data: any
    try {
      data = isJson ? await response.json() : await response.text()
    } catch (error) {
      data = null
    }

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 401) {
        // Unauthorized - token expired or invalid
        console.warn('⚠️ 401 Unauthorized - Token expired, redirecting to login')
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('auth-storage')
        window.location.href = '/auth/login'
      }

      // 403 Forbidden - 权限不足，不跳转登录页
      if (response.status === 403) {
        console.warn('⚠️ 403 Forbidden - Insufficient permissions')
      }

      return {
        success: false,
        message: data?.message || `HTTP ${response.status}: ${response.statusText}`,
        error: data?.error || response.statusText,
      }
    }

    // If the response already has success/message/data structure, return it as-is
    if (data && typeof data === 'object' && 'success' in data && 'message' in data) {
      return {
        success: data.success,
        message: data.message,
        data: data.data,
        error: data.error,
      }
    }

    // Otherwise, wrap the response
    return {
      success: true,
      message: data?.message || 'Success',
      data: data?.data || data,
    }
  }

  async get<T>(endpoint: string, options?: { params?: Record<string, any> }): Promise<ApiResponse<T>> {
    let url: URL
    
    // 如果 endpoint 已经包含查询参数，直接使用
    if (endpoint.includes('?')) {
      url = new URL(`${this.baseURL}/functions/v1${endpoint}`)
    } else {
      url = new URL(`${this.baseURL}/functions/v1${endpoint}`)
      
      // Handle params from options object
      const params = options?.params
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value))
          }
        })
      }
    }

    try {
      const authHeaders = this.getAuthHeaders()
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          ...this.defaultHeaders,
          ...authHeaders,
        },
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      console.error('API GET error:', error)
      return {
        success: false,
        message: '网络请求失败',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async post<T>(endpoint: string, data?: any, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}/functions/v1${endpoint}`, {
        method: 'POST',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
          ...customHeaders,
        },
        body: data ? JSON.stringify(data) : undefined,
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      console.error('API POST error:', error)
      return {
        success: false,
        message: '网络请求失败',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}/functions/v1${endpoint}`, {
        method: 'PUT',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
        body: data ? JSON.stringify(data) : undefined,
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      console.error('API PUT error:', error)
      return {
        success: false,
        message: '网络请求失败',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}/functions/v1${endpoint}`, {
        method: 'DELETE',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      console.error('API DELETE error:', error)
      return {
        success: false,
        message: '网络请求失败',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}/functions/v1${endpoint}`, {
        method: 'PATCH',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
        body: data ? JSON.stringify(data) : undefined,
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      console.error('API PATCH error:', error)
      return {
        success: false,
        message: '网络请求失败',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  // Upload file
  async upload<T>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, String(value))
        })
      }

      const response = await fetch(`${this.baseURL}/functions/v1${endpoint}`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders(),
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData,
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      console.error('API UPLOAD error:', error)
      return {
        success: false,
        message: '文件上传失败',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  // Admin authentication
  async adminLogin(email: string, password: string): Promise<ApiResponse<any>> {
    return this.post('/admin-auth-login', { email, password })
  }

  // Admin user management (for creating admin/operator users)
  async createAdminUser(userData: any): Promise<ApiResponse<any>> {
    return this.post('/admin-user-create', userData)
  }

  // User management
  async getUsersList(params?: URLSearchParams): Promise<ApiResponse<any>> {
    const endpoint = params ? `/admin-users-list?${params}` : '/admin-users-list'
    return this.get(endpoint)
  }

  async getUserDetail(userId: string): Promise<ApiResponse<any>> {
    return this.get('/admin-user-detail', {
      params: { user_id: userId } 
    })
  }

  async createUser(userData: any): Promise<ApiResponse<any>> {
    return this.post('/admin-user-manage?action=create', userData)
  }

  async updateUser(userData: any): Promise<ApiResponse<any>> {
    return this.put('/admin-user-manage?action=update', userData)
  }

  async updateUserStatus(userId: string, isActive: boolean, reason?: string): Promise<ApiResponse<any>> {
    return this.put('/admin-user-manage?action=status', {
      user_id: userId,
      is_active: isActive,
      reason
    })
  }

  async batchCreateUsers(operatorId: string, count: number, initialBalance?: number): Promise<ApiResponse<any>> {
    return this.post('/admin-user-manage?action=batch-create', {
      operator_id: operatorId,
      count,
      initial_balance: initialBalance
    })
  }

  // Operator management
  async getOperatorsList(params?: URLSearchParams): Promise<ApiResponse<any>> {
    const endpoint = params ? `/admin-operators-list?${params}` : '/admin-operators-list'
    return this.get(endpoint)
  }

  async getOperatorDetail(operatorId: string): Promise<ApiResponse<any>> {
    return this.get('/admin-operator-detail', {
      params: { operator_id: operatorId } 
    })
  }

  async createOperator(operatorData: any): Promise<ApiResponse<any>> {
    return this.post('/admin-operator-manage?action=create', operatorData)
  }

  async updateOperator(operatorData: any): Promise<ApiResponse<any>> {
    return this.put('/admin-operator-manage?action=update', operatorData)
  }

  async updateOperatorStatus(operatorId: string, isActive: boolean, reason?: string): Promise<ApiResponse<any>> {
    return this.put('/admin-operator-manage?action=status', {
      operator_id: operatorId,
      is_active: isActive,
      reason
    })
  }

  // Withdrawal management
  async getWithdrawalsList(params?: URLSearchParams): Promise<ApiResponse<any>> {
    const endpoint = params ? `/admin-withdrawals?${params}` : '/admin-withdrawals'
    return this.get(endpoint)
  }

  async getWithdrawalDetail(withdrawalId: string): Promise<ApiResponse<any>> {
    return this.get(`/admin-withdrawal-detail?withdrawal_id=${withdrawalId}`)
  }

  async approveWithdrawal(data: {
    withdrawal_id: string
    action: 'finance_approve' | 'audit_approve' | 'reject'
    notes?: string
    rejection_reason?: string
  }): Promise<ApiResponse<any>> {
    return this.post('/admin-withdrawal-approve', data)
  }

  async processWithdrawalPayment(withdrawalId: string, paymentProof: string): Promise<ApiResponse<any>> {
    return this.post('/admin-withdrawal-payment', {
      withdrawal_id: withdrawalId,
      payment_proof: paymentProof
    })
  }

  // Deposit management
  async getDepositsList(params?: URLSearchParams): Promise<ApiResponse<any>> {
    const endpoint = params ? `/admin-deposits?${params}` : '/admin-deposits'
    return this.get(endpoint)
  }

  async getDepositDetail(depositId: string): Promise<ApiResponse<any>> {
    return this.get(`/admin-deposits?id=${depositId}`)
  }

  // Trade management
  async getTradesList(params?: URLSearchParams): Promise<ApiResponse<any>> {
    const endpoint = params ? `/admin-trades?${params}` : '/admin-trades'
    return this.get(endpoint)
  }

  async getTradeDetail(tradeId: string): Promise<ApiResponse<any>> {
    return this.get(`/admin-trade-detail?trade_id=${tradeId}`)
  }

  // Fund flows and audit
  async getFundFlows(params?: URLSearchParams): Promise<ApiResponse<any>> {
    const endpoint = params ? `/admin-fund-flows?${params}` : '/admin-fund-flows'
    return this.get(endpoint)
  }

  async generateAuditReport(data: {
    report_type: 'daily' | 'weekly' | 'monthly' | 'custom'
    date_from: string
    date_to: string
    include_details?: boolean
  }): Promise<ApiResponse<any>> {
    return this.post('/admin-audit-reports', data)
  }

  async getReconciliationStatus(): Promise<ApiResponse<any>> {
    return this.get('/admin-reconciliation')
  }

  // System parameters management
  async getSystemParameters(): Promise<ApiResponse<any>> {
    return this.get('/admin-system-parameters')
  }

  async getSystemParametersByCategory(category: string): Promise<ApiResponse<any>> {
    return this.get('/admin-system-parameters', { params: { category } })
  }

  async getSystemParameter(key: string): Promise<ApiResponse<any>> {
    return this.get('/admin-system-parameters', { params: { key } })
  }

  async updateSystemParameter(key: string, value: string): Promise<ApiResponse<any>> {
    try {
      const url = new URL(`${this.baseURL}/functions/v1/admin-system-parameters`)
      url.searchParams.append('key', key)
      
      const response = await fetch(url.toString(), {
        method: 'PUT',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({ param_value: value }),
      })

      return this.handleResponse<any>(response)
    } catch (error) {
      console.error('API PUT error:', error)
      return {
        success: false,
        message: '网络请求失败',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<any>> {
    return this.post('/backend-health-check', { check_type: 'basic' })
  }

  // Payment channel management
  async getPaymentChannels(): Promise<ApiResponse<any>> {
    return this.get('/admin-payment-channels')
  }

  async getPaymentChannel(id: string): Promise<ApiResponse<any>> {
    return this.get('/admin-payment-channels', { params: { id } })
  }

  async updatePaymentChannel(id: string, updates: any): Promise<ApiResponse<any>> {
    return this.patch(`/admin-payment-channels?id=${id}`, updates)
  }

  async togglePaymentChannel(id: string, isEnabled: boolean): Promise<ApiResponse<any>> {
    return this.patch(`/admin-payment-channels?id=${id}&action=toggle`, { is_enabled: isEnabled })
  }

  async updatePaymentChannelSort(id: string, direction: 'up' | 'down'): Promise<ApiResponse<any>> {
    return this.patch(`/admin-payment-channels?id=${id}&action=sort`, { direction })
  }

  // OKX order management
  async getAdminOKXOrders(params?: {
    user_id?: string | null
    operator_id?: string | null
    order_type?: 'user' | 'operator' | null
    status?: string | null
    search?: string | null
    limit?: number
    offset?: number
  }): Promise<ApiResponse<any>> {
    return this.post('/admin-okx-orders', {
      action: 'list',
      ...params
    })
  }

  async getAdminOKXOrdersStats(params?: {
    order_type?: 'user' | 'operator' | null
  }): Promise<ApiResponse<any>> {
    return this.post('/admin-okx-orders', {
      action: 'stats',
      ...params
    })
  }

  async updateOKXOrderStatus(orderId: string, status: string, remark?: string): Promise<ApiResponse<any>> {
    return this.post('/admin-okx-orders', {
      action: 'update_status',
      order_id: orderId,
      new_status: status,
      remark
    })
  }
}

export const apiClient = new ApiClient()